/**
 * 版本检测工具
 * 用于检测客户端代码版本是否过期
 */

class VersionChecker {
  private currentVersion: string;
  private readonly VERSION_KEY = "app_version";
  private notificationShown: boolean = false;

  constructor() {
    this.currentVersion = import.meta.env.VITE_APP_VERSION || "1.0.0";
  }

  /**
   * 检查版本是否过期
   */
  checkVersion(): boolean {
    const storedVersion = localStorage.getItem(this.VERSION_KEY);

    if (!storedVersion) {
      // 首次访问，存储当前版本
      this.storeCurrentVersion();
      return false;
    }

    if (storedVersion !== this.currentVersion) {
      // 版本不一致，需要更新
      // 立即更新存储的版本号，确保下次刷新不再提示
      this.storeCurrentVersion();
      return true;
    }

    return false;
  }

  /**
   * 显示更新提示
   */
  async showUpdateNotification(): Promise<void> {
    if (this.notificationShown) {
      return;
    }

    this.notificationShown = true;

    try {
      const { ElNotification } = await import("element-plus");

      ElNotification({
        title: "版本更新提醒",
        message: `检测到新版本可用，刷新可立即获取最新功能。`,
        type: "warning",
        duration: 0,
        position: "top-right",
        showClose: true,
        customClass: "version-update-notification",
        dangerouslyUseHTMLString: false,
        onClick: () => {
          window.location.reload();
        },
        onClose: () => {
          this.notificationShown = false;
        },
        appendTo: document.body,
        zIndex: 2000,
      });
    } catch (error) {
      console.warn("Element Plus Notification 加载失败，使用原生 confirm:", error);
      // 降级方案：使用原生 confirm
      const shouldUpdate = confirm("检测到新版本可用，是否立即刷新页面获取最新功能？");
      if (shouldUpdate) {
        window.location.reload();
      }
      this.notificationShown = false;
    }
  }

  /**
   * 存储当前版本
   */
  private storeCurrentVersion(): void {
    localStorage.setItem(this.VERSION_KEY, this.currentVersion);
  }

  /**
   * 强制清除缓存并刷新
   */
  forceRefresh(): void {
    localStorage.removeItem(this.VERSION_KEY);
    sessionStorage.clear();
    window.location.reload();
  }
}

export const versionChecker = new VersionChecker();
